import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { markdownToSlate } from "./SharedTransforms";
import { MarkdownBadge } from "./MarkdownEditor";
import { VisualizationPreview } from "./VisualizationPreview";
import { KonvaPrimitive } from "./KonvaPrimitive";

function renderLeafNode(leaf, key) {
  if (leaf.type === "line-break") {
    return <br key={key} />;
  }
  const text = leaf.text ?? "";
  let node = text;
  if (leaf.code) {
    node = (
      <code className="rounded bg-slate-100 px-1 py-0.5 text-[12px] text-slate-700 dark:bg-white/10 dark:text-slate-100">
        {node}
      </code>
    );
  }
  if (leaf.bold) {
    node = <strong>{node}</strong>;
  }
  if (leaf.italic) {
    node = <em>{node}</em>;
  }
  return (
    <span key={key} className="whitespace-pre-wrap">
      {node}
    </span>
  );
}

function renderMarkdownNodes(nodes, options = {}) {
  if (!Array.isArray(nodes)) {
    return null;
  }
  return nodes.map((node, idx) => renderMarkdownNode(node, `${options.keyPrefix ?? "md"}_${idx}`, options));
}

function renderMarkdownNode(node, key, options) {
  if (!node) {
    return null;
  }
  if (typeof node.text === "string" || node.type === "line-break") {
    return renderLeafNode(node, key);
  }
  const headingClasses = {
    1: "text-2xl",
    2: "text-xl",
    3: "text-lg",
    4: "text-base",
    5: "text-sm",
    6: "text-xs",
  };
  const children = renderMarkdownNodes(node.children ?? [], {
    ...options,
    keyPrefix: key,
    isHeaderRow: node.type === "table-row" ? Boolean(node.isHeader) : options.isHeaderRow,
  });
  switch (node.type) {
    case "paragraph":
      return (
        <p key={key} className="mb-2 last:mb-0 whitespace-pre-wrap">
          {children}
        </p>
      );
    case "heading": {
      const level = Math.min(Math.max(node.level ?? 2, 1), 6);
      return (
        <h2 key={key} className={clsx(headingClasses[level], "font-semibold my-3")}> 
          {children}
        </h2>
      );
    }
    case "unordered-list":
      return (
        <ul key={key} className="pl-6 list-disc space-y-1">
          {children}
        </ul>
      );
    case "ordered-list": {
      const start = node.start;
      return (
        <ol
          key={key}
          className="pl-6 list-decimal space-y-1"
          {...(start && start !== 1 ? { start } : {})}
        >
          {children}
        </ol>
      );
    }
    case "list-item":
      return <li key={key}>{children}</li>;
    case "table":
      return (
        <table key={key} className="w-full table-auto border-collapse text-sm">
          {children}
        </table>
      );
    case "table-row":
      return (
        <tr key={key} className={node.isHeader ? "bg-slate-100 font-semibold" : undefined}>
          {children}
        </tr>
      );
    case "table-cell": {
      const Tag = options.isHeaderRow ? "th" : "td";
      return (
        <Tag key={key} className="border border-slate-200 px-2 py-1 align-top">
          {children}
        </Tag>
      );
    }
    case "link":
      return (
        <a
          key={key}
          href={node.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline cursor-pointer"
        >
          {children}
        </a>
      );
    case "badge":
      return (
        <MarkdownBadge
          key={key}
          badgeType={node.badgeType}
          badgeData={node.badgeData}
          actionCallback={options.actionCallback}
          customRenderers={options.badgeRenderers}
          compact={options.compactMode}
        />
      );
    default:
      return (
        <span key={key} className="whitespace-pre-wrap">
          {children}
        </span>
      );
  }
}

export function MarkdownContent({ content, badgeRenderers, actionCallback, compactMode }) {
  const nodes = useMemo(() => markdownToSlate(content ?? ""), [content]);
  return (
    <div className="w-full text-slate-900 dark:text-slate-100">
      {renderMarkdownNodes(nodes, { badgeRenderers, actionCallback, compactMode })}
    </div>
  );
}

function ChatPreview({ content }) {
  if (!content) {
    return null;
  }
  let parsed;
  try {
    parsed = JSON.parse(content);
  } catch (error) {
    parsed = null;
  }
  if (!parsed?.views) {
    return null;
  }
  return (
    <div className="p-2">
      {parsed.views.map((entry, idx) => (
        <VisualizationPreview key={idx} {...entry} size="small" />
      ))}
    </div>
  );
}

function ChatKonvaPreview({ primitive, flowOutputNames, width }) {
  if (!primitive) {
    return null;
  }
  return (
    <div className="flex w-full justify-start">
      <div className="w-full rounded-2xl border border-slate-200 bg-white/80 px-3 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-900/50">
        <KonvaPrimitive
          primitive={primitive}
          flowOutputNames={flowOutputNames}
          width={width}
          enableZoom={false}
          enableDrag={false}
          enableTextSelection={true}
          preview={true}
        />
      </div>
    </div>
  );
}

function ChatBubble({ message, badgeRenderers, actionCallback, compactMode }) {
  const side = message?.role === "user" ? "right" : "left";
  const isRight = side === "right";
  const content = message?.content ?? "";
  const bubbleClass = isRight
    ? "rounded-lg bg-slate-200 px-2 py-1 max-w-[60%] text-slate-900 dark:bg-slate-800 dark:text-slate-100 dark:border dark:border-white/10"
    : "p-2 max-w-[90%] text-slate-900 dark:bg-slate-900/40 dark:text-white dark:border dark:border-white/10";
  return (
    <div className={clsx("flex w-full", isRight ? "justify-end" : "justify-start")}>
      <div className={bubbleClass}>
        <MarkdownContent
          content={content}
          badgeRenderers={badgeRenderers}
          actionCallback={actionCallback}
          compactMode={compactMode}
        />
      </div>
    </div>
  );
}

function ChatBadge({ badgeType, badgeData, badgeRenderers, actionCallback, compactMode }) {
  return (
    <div className="flex w-full justify-start">
      <MarkdownBadge
        badgeType={badgeType}
        badgeData={badgeData}
        actionCallback={actionCallback}
        customRenderers={badgeRenderers}
        compact={compactMode}
      />
    </div>
  );
}

export default function ChatTimeline({
  messages,
  badgeRenderers,
  actionCallback,
  compactMode,
  statusBadge,
}) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const visible = useMemo(
    () => (Array.isArray(messages) ? messages.filter((entry) => !entry?.hidden) : []),
    [messages]
  );
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const nextWidth = Math.floor(entry.contentRect?.width ?? 0);
      setContainerWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const renderKonvaGrid = (items, key) => {
    if (!items.length) {
      return null;
    }
    const width = containerWidth || 840;
    const columns = width >= 960 ? 4 : width >= 720 ? 3 : 2;
    const gap = 12;
    const itemWidth = Math.max(220, Math.floor((width - gap * (columns - 1)) / columns));
    return (
      <div
        key={key}
        className="grid w-full gap-3"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {items.map((message, idx) => (
          <ChatKonvaPreview
            key={`konva_${message.konvaPreview?.primitive?.id ?? idx}`}
            primitive={message.konvaPreview?.primitive}
            flowOutputNames={message.konvaPreview?.flowOutputNames}
            width={itemWidth}
          />
        ))}
      </div>
    );
  };
  return (
    <div ref={containerRef} className="mx-auto flex w-full max-w-[840px] flex-col gap-3 px-2 py-3 sm:px-4">
      {(() => {
        const rendered = [];
        let idx = 0;
        while (idx < visible.length) {
          const message = visible[idx];
          if (message?.konvaPreview) {
            const group = [];
            while (idx < visible.length && visible[idx]?.konvaPreview) {
              group.push(visible[idx]);
              idx += 1;
            }
            rendered.push(renderKonvaGrid(group, `konva_group_${idx}`));
            continue;
          }
          if (message?.preview) {
            rendered.push(<ChatPreview key={`preview_${idx}`} content={message.content} />);
            idx += 1;
            continue;
          }
          if (message?.badgeType) {
            rendered.push(
              <ChatBadge
                key={`badge_${message.badgeType}_${idx}`}
                badgeType={message.badgeType}
                badgeData={message.badgeData}
                badgeRenderers={badgeRenderers}
                actionCallback={actionCallback}
                compactMode={compactMode}
              />
            );
            idx += 1;
            continue;
          }
          rendered.push(
            <ChatBubble
              key={`msg_${idx}`}
              message={message}
              badgeRenderers={badgeRenderers}
              actionCallback={actionCallback}
              compactMode={compactMode}
            />
          );
          idx += 1;
        }
        return rendered;
      })()}
      {statusBadge && (
        <div className="flex w-full justify-start">
          <MarkdownBadge
            badgeType={statusBadge}
            badgeData={null}
            actionCallback={actionCallback}
            customRenderers={badgeRenderers}
            compact={compactMode}
          />
        </div>
      )}
    </div>
  );
}
